import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { errorView, isTransportError } from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import { grantOwnCashier, signInOwnCashier } from '../api/ownCashiersApi'
import { proofGranted } from './cashierAccessSlice'
import { cashierChosen } from './startSlice'
import type { OwnSignInOutcome, OwnWorker } from './types'

export type OwnRefusal = Extract<OwnSignInOutcome, { ok: false }>

function refusal(error: unknown): OwnRefusal {
  const view = errorView(error)
  if (view.code === 'cashier-credential-locked' || view.code === 'cashier-locked') {
    const after = isTransportError(error) ? error.details?.['retryAfterSeconds'] : undefined
    const seconds = typeof after === 'number' && Number.isFinite(after) ? after : 300
    return { ok: false, message: view.message, lockedUntil: new Date(Date.now() + seconds * 1000).toISOString() }
  }
  // Текст отказа службы — дословно (решение 19.10.2026): «Неверный код кассира», «Кассиру закрыт допуск к РМК…».
  return { ok: false, message: view.message, lockedUntil: null }
}

/** Пропуск РМК на кассира этого кабинета или слова отказа службы (при блокировке — до какого времени). */
export type OwnGrantOutcome = { ok: true; grant: string; expiresAt: string } | OwnRefusal

/**
 * Код выбранного кассира нашей системы → пропуск на его кассира в этом кабинете (RP-CU4): служба кассиров проверяет код (`people/sign-in`) и
 * выдаёт пропуск по билету входа этой вкладки (`people/grants`). Общая часть входа в «Начале работы» и переноса рабочего места из другой
 * вкладки (К-120). Код живёт только в замыкании и теле запроса.
 */
export function ownCashierGrantRequested({ resourceId, worker, credential }: { resourceId: string; worker: OwnWorker; credential: string }):
  ThunkAction<Promise<OwnGrantOutcome>, unknown, unknown, UnknownAction> {
  return async (dispatch, getState, extra) => {
    if (credential.trim() === '') return { ok: false, message: 'Введите код кассира.', lockedUntil: null }
    try {
      const signed = await signInOwnCashier({ code: credential, cashierId: worker.cashierId })(dispatch, getState, extra)
      if (!signed.ok) return refusal(signed.error)
      const place = signed.data.workplaces.find((item) => sameId(item.resourceId, resourceId) && sameId(item.sourceCashierId, worker.sourceCashierId))
      if (place === undefined) {
        return { ok: false, lockedUntil: null,
          message: 'Кассир не связан с кассиром этого кабинета или у вас нет права работать здесь на кассе. Обратитесь к администратору: вкладка «Кассиры».' }
      }
      const granted = await grantOwnCashier({ ticket: signed.data.ticket, resourceId, sourceCashierId: place.sourceCashierId })(dispatch, getState, extra)
      if (!granted.ok) return refusal(granted.error)
      return { ok: true, grant: granted.data.grant, expiresAt: granted.data.expiresAt }
    } catch (error) {
      return refusal(error)
    }
  }
}

/**
 * Вход кассира нашей системы в «Начале работы» (RP-CU4): пропуск на его кассира в этом кабинете (`ownCashierGrantRequested`). Дальше всё как
 * раньше: кассир выбран и подтверждён, `session/open` идёт с этим пропуском, поэтому открытая смена продолжается («Продолжить смену»), а не
 * закрывается.
 */
export function ownCashierSignInRequested({ resourceId, worker, credential }: { resourceId: string; worker: OwnWorker; credential: string }):
  ThunkAction<Promise<OwnSignInOutcome>, unknown, unknown, UnknownAction> {
  return async (dispatch, getState, extra) => {
    const granted = await ownCashierGrantRequested({ resourceId, worker, credential })(dispatch, getState, extra)
    if (!granted.ok) return granted
    dispatch(cashierChosen({ resourceId, cashier: { kind: 'cashier', id: worker.sourceCashierId, name: worker.sourceCashierName || worker.displayName } }))
    dispatch(proofGranted({ resourceId, cashierId: worker.sourceCashierId, proof: granted.grant, expiresAt: granted.expiresAt }))
    return { ok: true }
  }
}
